// ============================================
// NRS.09 — Conversation Audit & Provenance Service
// ============================================
// Extended from: src/services/conversationAuditService.ts
// Spec: hlx-nrs-09-conversation-audit-provenance.md (LOCKED)
// Contract: Fire-and-forget (NEVER blocks patient conversation),
//           flagging pipeline, human reviewer queue (RN minimum),
//           PRO.12 chain emission, weekly drift reporting,
//           recall sampling (1% stratified), HIPAA 6yr retention
// ============================================

import type {
  AuditRecord,
  AuditFlagSource,
  AuditReviewStatus,
  ReviewerAction,
  EffectivenessMetrics,
  WeeklyDriftReport,
  RecallSample,
  ConversationAuditMetrics,
} from '../types/conversationAudit';
import type { FilterResult, FilterViolation } from '../types/safetyOutputFilter';

// ── Metrics State ──

let _metrics: ConversationAuditMetrics = {
  totalCalls: 0,
  successRate: 1,
  avgLatencyMs: 0,
  errorsByType: {},
  accuracyOnGoldStandard: 0,
};
let _totalLatency = 0;
let _successCount = 0;

// ── Injectable Interface ──

export interface AuditFirestoreProvider {
  writeAuditRecord(record: AuditRecord): Promise<void>;
  getAuditRecords(sessionId: string): Promise<AuditRecord[]>;
  getPendingReviews(): Promise<AuditRecord[]>;
  updateReviewStatus(
    recordId: string,
    status: AuditReviewStatus,
    verdict?: string,
    notes?: string,
  ): Promise<void>;
  writeProvenanceChain(chainId: string, data: Record<string, unknown>): Promise<void>;
}

// ── Audit Service ──

export class ConversationAuditService {
  private firestoreProvider: AuditFirestoreProvider;
  private sessionAuditRecords: Map<string, AuditRecord[]> = new Map();

  constructor(firestoreProvider: AuditFirestoreProvider) {
    this.firestoreProvider = firestoreProvider;
  }

  /**
   * Flag a conversation for human review.
   * FIRE-AND-FORGET: This method NEVER blocks the patient conversation.
   * Errors are logged but swallowed.
   */
  flagForReview(
    sessionId: string,
    patientId: string,
    filterResult: FilterResult,
    agentRole: 'nurse' | 'captain',
    outcome: 'flagged' | 'regenerated' | 'blocked',
    turnNumber: number,
  ): void {
    // Fire-and-forget — mirrors existing conversationAuditService.ts pattern
    this._writeFlagRecord(sessionId, patientId, filterResult, agentRole, outcome, turnNumber)
      .catch((err) => {
        console.error('[NRS.09] Failed to write audit record:', err);
      });
  }

  /**
   * Record a missed violation found by human review.
   */
  async recordMissedViolation(
    sessionId: string,
    patientId: string,
    turnNumber: number,
    expectedRuleId: string,
    description: string,
  ): Promise<void> {
    const record = createAuditRecord(
      sessionId,
      patientId,
      'clinical_boundary',
      [expectedRuleId],
      [description],
      'block',
      'regenerate',
      turnNumber,
      'nurse',
      'flagged',
    );
    record.reviewStatus = 'pending';
    await this.firestoreProvider.writeAuditRecord(record);
  }

  /**
   * Emit PRO.12 provenance chain reference.
   * Links Nurse audit records to Protocol audit trail.
   */
  async emitProvenanceChain(
    sessionId: string,
    auditRecordIds: string[],
  ): Promise<string> {
    const chainId = `prov-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    await this.firestoreProvider.writeProvenanceChain(chainId, {
      sessionId,
      auditRecordIds,
      source: 'nurse',
      createdAt: new Date().toISOString(),
    });
    return chainId;
  }

  /**
   * Get pending reviews for the human reviewer queue.
   * Minimum qualification: RN.
   */
  async getPendingReviews(): Promise<AuditRecord[]> {
    return this.firestoreProvider.getPendingReviews();
  }

  /**
   * Submit a reviewer action.
   */
  async submitReview(
    action: ReviewerAction,
  ): Promise<void> {
    await this.firestoreProvider.updateReviewStatus(
      action.auditRecordId,
      'reviewed',
      action.verdict,
      action.notes,
    );
  }

  /**
   * Generate a weekly drift report.
   */
  generateWeeklyDriftReport(
    records: AuditRecord[],
    weekStart: string,
    weekEnd: string,
    totalConversations: number,
  ): WeeklyDriftReport {
    const driftRecords = records.filter((r) => r.flagSource === 'drift_monitoring');
    const driftByDimension: Record<string, number> = {};
    const ruleHits: Record<string, number> = {};

    for (const record of records) {
      for (const rule of record.violatedRules) {
        ruleHits[rule] = (ruleHits[rule] || 0) + 1;
        if (rule.startsWith('DRIFT-')) {
          const dim = rule.replace('DRIFT-', '').toLowerCase();
          driftByDimension[dim] = (driftByDimension[dim] || 0) + 1;
        }
      }
    }

    const topRules = Object.entries(ruleHits)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([ruleId, count]) => ({ ruleId, count }));

    const reviewed = records.filter((r) => r.reviewStatus === 'reviewed');

    return {
      weekStart,
      weekEnd,
      totalConversations,
      driftEventsDetected: driftRecords.length,
      driftByDimension,
      topViolatedRules: topRules,
      reviewerFeedbackSummary: {
        confirmed: reviewed.filter((r) => r.outcome === 'blocked').length,
        falsePositive: reviewed.filter((r) => r.outcome === 'flagged').length,
        missed: 0, // Populated by cross-referencing missed violation records
      },
    };
  }

  /**
   * Select 1% stratified sample for recall measurement.
   */
  selectRecallSample(
    sessionIds: string[],
    samplingRate: number = 0.01,
  ): RecallSample[] {
    const sampleSize = Math.max(1, Math.floor(sessionIds.length * samplingRate));
    const shuffled = [...sessionIds].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, sampleSize);

    return selected.map((sessionId) => ({
      sampleId: `sample-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      sessionId,
      samplingRate,
      selectedAt: new Date(),
      reviewComplete: false,
      missedViolationsFound: 0,
    }));
  }

  /**
   * Calculate effectiveness metrics for a date range.
   */
  calculateEffectiveness(
    records: AuditRecord[],
    missedViolationCount: number,
    startDate: string,
    endDate: string,
  ): EffectivenessMetrics {
    const totalFlagged = records.length;
    const confirmed = records.filter(
      (r) => r.reviewStatus === 'reviewed' && r.outcome === 'blocked',
    ).length;
    const falsePositives = records.filter(
      (r) => r.reviewStatus === 'reviewed' && r.outcome === 'flagged',
    ).length;

    const precision = totalFlagged > 0 ? confirmed / totalFlagged : 1;
    const totalReal = confirmed + missedViolationCount;
    const recall = totalReal > 0 ? confirmed / totalReal : null;

    const ruleHitCounts: Record<string, number> = {};
    for (const record of records) {
      for (const rule of record.violatedRules) {
        ruleHitCounts[rule] = (ruleHitCounts[rule] || 0) + 1;
      }
    }

    return {
      startDate,
      endDate,
      totalFlagged,
      confirmed,
      falsePositives,
      missedViolations: missedViolationCount,
      precision,
      recall,
      ruleHitCounts,
    };
  }

  // ── Private Methods ──

  private async _writeFlagRecord(
    sessionId: string,
    patientId: string,
    filterResult: FilterResult,
    agentRole: 'nurse' | 'captain',
    outcome: 'flagged' | 'regenerated' | 'blocked',
    turnNumber: number,
  ): Promise<void> {
    const startTime = Date.now();
    _metrics.totalCalls++;

    const flagSource = determineFlagSource(filterResult.violations, outcome);
    const maxSeverity = computeMaxSeverity(filterResult.violations);

    const record = createAuditRecord(
      sessionId,
      patientId,
      flagSource,
      filterResult.violations.map((v) => v.ruleId),
      filterResult.violations.map((v) => v.message),
      maxSeverity,
      filterResult.driftAction,
      turnNumber,
      agentRole,
      outcome,
    );

    await this.firestoreProvider.writeAuditRecord(record);

    // Track per-session records
    const existing = this.sessionAuditRecords.get(sessionId) || [];
    existing.push(record);
    this.sessionAuditRecords.set(sessionId, existing);

    _successCount++;
    _totalLatency += Date.now() - startTime;
  }
}

// ── Helpers ──

function createAuditRecord(
  sessionId: string,
  patientId: string,
  flagSource: AuditFlagSource,
  violatedRules: string[],
  violationMessages: string[],
  maxSeverity: 'info' | 'warn' | 'block',
  driftAction: string,
  turnNumber: number,
  agentRole: 'nurse' | 'captain',
  outcome: 'flagged' | 'regenerated' | 'blocked',
): AuditRecord {
  return {
    recordId: `audit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    sessionId,
    patientId,
    detectedAt: new Date(),
    flagSource,
    violatedRules,
    violationMessages,
    maxSeverity,
    driftAction,
    turnNumber,
    agentRole,
    outcome,
    reviewStatus: 'pending',
  };
}

function determineFlagSource(
  violations: FilterViolation[],
  outcome: 'flagged' | 'regenerated' | 'blocked',
): AuditFlagSource {
  if (outcome === 'blocked') return 'regeneration_exhausted';
  const hasClinical = violations.some((v) => v.category === 'clinical_boundary');
  const hasProfessional = violations.some((v) => v.category === 'professional_distance');
  const hasDrift = violations.some((v) => v.ruleId.startsWith('DRIFT-'));
  if (hasClinical) return 'clinical_boundary';
  if (hasProfessional) return 'professional_distance';
  if (hasDrift) return 'drift_monitoring';
  return 'drift_monitoring';
}

function computeMaxSeverity(violations: FilterViolation[]): 'info' | 'warn' | 'block' {
  if (violations.some((v) => v.severity === 'block')) return 'block';
  if (violations.some((v) => v.severity === 'warn')) return 'warn';
  return 'info';
}

// ── Factory ──

export function createConversationAudit(
  firestoreProvider: AuditFirestoreProvider,
): ConversationAuditService {
  return new ConversationAuditService(firestoreProvider);
}

// ── Metrics ──

export function getMetrics(): ConversationAuditMetrics {
  return {
    ..._metrics,
    successRate: _metrics.totalCalls > 0 ? _successCount / _metrics.totalCalls : 1,
    avgLatencyMs: _metrics.totalCalls > 0 ? _totalLatency / _metrics.totalCalls : 0,
  };
}
